/* eslint-disable react-refresh/only-export-components */
import { ReactNode, useEffect } from 'react';
import { PropertyProvider } from '../../features/property/PropertyContext';
import { PricingProvider } from '../../features/pricing/PricingContext';
import { BookingProvider } from '../../features/booking/BookingContext';
import { IntegrationProvider } from '../../features/integration/IntegrationContext';
import { UserProvider } from './UserContext';
import { initializeBookingPricingIntegration } from '../../services/bookingPricingIntegration';

/**
 * AppProvider
 * Composes all domain contexts into a single provider tree
 *
 * Context Hierarchy:
 * - UserProvider (outermost - provides auth and user state)
 *   - PropertyProvider (property and room type management)
 *     - PricingProvider (pricing rules and data)
 *       - BookingProvider (reservations and metrics)
 *         - IntegrationProvider (PMS integrations)
 *
 * This hierarchy ensures that:
 * 1. User context is available to all other contexts
 * 2. Property context is available to pricing, booking, and integration
 * 3. Each context can access contexts above it in the tree
 */
export const AppProvider = ({ children }: { children: ReactNode }) => {
  console.log('[AppProvider] Initializing new provider architecture...');

  // Initialize booking-pricing integration on mount
  useEffect(() => {
    initializeBookingPricingIntegration();
  }, []);

  return (
    <UserProvider>
      <PropertyProvider>
        <PricingProvider>
          <BookingProvider>
            <IntegrationProvider>{children}</IntegrationProvider>
          </BookingProvider>
        </PricingProvider>
      </PropertyProvider>
    </UserProvider>
  );
};

/**
 * Export all context hooks for easy access
 */
export { useUser } from './UserContext';
export { useProperty } from '../../features/property/PropertyContext';
export { usePricing } from '../../features/pricing/PricingContext';
export { useBooking } from '../../features/booking/BookingContext';
export { useIntegration } from '../../features/integration/IntegrationContext';

/**
 * Export all types for convenience
 */
export type { User, UserPreferences, AuthStatus } from '../types';
export type { Property, RoomType } from '../../features/property/types';
export type { PricingData, Rule, Alert } from '../../features/pricing/types';
export type {
  Reservation,
  ReservationMetrics,
} from '../../features/booking/types';
export type { Integration, SyncLog } from '../../features/integration/types';
